import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { useSpring, animated } from 'react-spring';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import { useIsMobileOrTablet } from '../../../shared/theme';
import SearchBarContext, { reducer, defaultState } from './search-bar-elements/context';
import InputBox from './search-bar-elements/input-box';
import StockTick from './search-bar-elements/stock-tick';
import Cross from './search-bar-elements/cross';
import SearchLivewireButton from './search-bar-elements/search-livewire-button';
import MagnifyGlassIcon from './search-bar-elements/magnify-glass-icon';
import ResultBox from './search-bar-elements/result-box';
import theme from '../../../../assets/stylesheets/_settings.global.scss';
import { useGetRecentSearches } from '../../../services/search/recent-service';
import useGetSuggestedSearches from '../../../services/search/suggested-service';
import breakpoints from '../../../shared/breakpoints';

const Container = styled(animated.form)`
  ${props => props.pseudomobile === 'true' && `
    right: '24px';
  `};

  ${props => props.theme.isMobileOrTablet && `
    right: 8px;
  `};

  position: absolute;
  z-index: ${props => (props.theme.isMobile ? 512 : 200)};
  height: ${props => (props.theme.isMobile ? 50 : 66)}px;
  line-height: ${props => (props.theme.isMobile ? 50 : 66)}px;
  background-color: ${props => props.theme.bodyColor};
`;

const fontSize = 14;
const widestGlyph = 9.875; // `W`
const relaxRatio = 1.4; // some characters won't be `W`
const longestTypeGlyph = 64; // `Contributor`

const getOptimumLen = w => {
  const avgGlyphWidth = (w < breakpoints.smallMax ? fontSize : widestGlyph) / relaxRatio;
  return Math.round((w - longestTypeGlyph) / avgGlyphWidth);
};

/* eslint-disable no-nested-ternary */
const animateWidth = s => (s.expanded ? s.width : (s.pseudoMobile ? 40 : 146));
const animateX = s => (s.pseudoMobile ? (s.showRecent ? 146 : 182) : (s.expanded ? s.width : 146));
/* eslint-enable no-nested-ternary */

export default function SearchBar({ isMobile, isUserSignedIn }) {
  const [state, dispatch] = React.useReducer(reducer, {
    ...defaultState,
    showRecent: isUserSignedIn,
    isMobile,
    pseudoMobile: isMobile,
  });
  const setWidth = React.useCallback(w => dispatch({ type: 'setWidth', payload: w }), [dispatch]);
  const setRecentResults = React.useCallback(
    d => dispatch({ type: 'setRecentSearchResult', payload: _.get(d, 'recent_searches', []) }),
    [dispatch],
  );
  const setSuggestedResults = React.useCallback(
    d => dispatch({ type: 'setSuggestionResult', payload: _.get(d, 'suggested_searches', []) }),
    [dispatch],
  );

  useChangeAwareEffect(({ did }) => {
    if (did.winWidth.notChange) {
      return;
    }
    let width;
    if (isMobile) {
      width = window.innerWidth - 8 * 2;
    } else {
      const primaryNavLeft = document.getElementById('primary-nav').getClientRects()[0].left;
      const restNavLeft = document.getElementById('rest-nav').getClientRects()[0].left;
      width = restNavLeft - primaryNavLeft;
    }
    setWidth(width);
  }, { winWidth: window.innerWidth });

  const { w, x } = useSpring({
    w: animateWidth(state),
    x: animateX(state),
    immediate: !state.animation,
  });

  const [, qRecent] = useGetRecentSearches();
  const [, qSuggest] = useGetSuggestedSearches();

  React.useEffect(() => {
    state.expanded && state.showRecent && qRecent(state.query)
      .then(({ data }) => setRecentResults(data))
      .catch(() => { /* throw away error */ });
  }, [state.expanded, state.query, state.showRecent, qRecent, setRecentResults]);

  React.useEffect(() => {
    state.expanded && state.showSuggestion && qSuggest(state.query, getOptimumLen(state.width))
      .then(({ data }) => setSuggestedResults(data))
      .catch(() => { /* throw away error */ });
  }, [
    state.expanded, state.query, state.width, state.showSuggestion, qSuggest, setSuggestedResults,
  ]);

  const containerBlured = e => {
    if (state.focus) {
      dispatch({ type: 'inputFocus', payload: false });
      dispatch({ type: 'focus', payload: false });
    } else {
      const { currentTarget } = e;
      setTimeout(() => {
        if (!currentTarget.contains(document.activeElement)) {
          dispatch({ type: 'inputFocus', payload: false });
          dispatch({ type: 'expand', payload: false });
          dispatch({ type: 'focus', payload: false });
        }
      }, 0);
    }
  };

  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <ThemeProvider theme={{ ...theme, isMobile, isMobileOrTablet }}>
      <SearchBarContext.Provider value={{ state, dispatch }}>
        <Container
          action="."
          onBlur={containerBlured}
          pseudomobile={state.pseudoMobile.toString()}
          className={isUserSignedIn ? 'user-signed-in' : ''}
          style={{
            width: w.interpolate(v => `${v}px`),
            ...(isMobile ? {} : { transform: x.interpolate(v => `translateX(-${v}px)`) }),
          }}
        >
          <InputBox />
          {!!state.showStockCodeTick && <StockTick />}
          <Cross />
          <SearchLivewireButton />
          {!state.pseudoMobile && <MagnifyGlassIcon />}
          {((state.rsearch.length > 0)
            || (state.showSuggestion && state.suggestions.length > 0)) && <ResultBox />}
        </Container>
      </SearchBarContext.Provider>
    </ThemeProvider>
  );
}

SearchBar.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  isUserSignedIn: PropTypes.bool.isRequired,
};
